import { type CaseRepository, Case, CaseSortCriteria, CaseListFilter, CaseSearchContext, CaseListCursor } from '@telespot/domain';
import { ParseBaseRepository } from '../../parse-base.repository';
import { CaseTopology, ParseCaseMapper } from './parse-case.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';
import { OrganizationTopology } from '../organization';
import { WorkspaceTopology } from './parse-workspace.mapper';
import { CaseTypeTopology } from '../protocols';

export class ParseCaseRepository extends ParseBaseRepository implements CaseRepository {
  private static readonly COUNT_CASES_PAGINATION_LIMIT = 25;

  private readonly mapper = new ParseCaseMapper(this.parse);

  public async getByUUID(uuid: string): Promise<Case> {
    const parseCase = await new this.parse.Query(CaseTopology.TABLE)
      .equalTo(CaseTopology.UUID, uuid)
      .first(this.options);

    return parseCase ? this.mapper.toDomain(parseCase) : undefined;
  }

  public async findByIds(...ids: string[]): Promise<Case[]> {
    const parseCases = await new this.parse.Query(CaseTopology.TABLE)
      .containedIn(ObjectTopology.ID, ids)
      .find(this.options);

    return parseCases.map((pc) => this.mapper.toDomain(pc));
  }

  public async findCreatorUserIdsInOrganizations(organizationIds: string[], userIds: string[]): Promise<string[]> {
    if (organizationIds.length === 0 || userIds.length === 0) return [];

    const ParseOrganization = this.subclasses.getSubclass(OrganizationTopology.TABLE);
    const organizationPointers = organizationIds.map((id) => ParseOrganization.createWithoutData(id));
    const userPointers = userIds.map((id) => this.parse.User.createWithoutData(id));

    const query = new this.parse.Query(CaseTopology.TABLE)
      .containedIn(CaseTopology.ORGANIZATION, organizationPointers)
      .containedIn(CaseTopology.CREATED_BY, userPointers)
      .select(CaseTopology.CREATED_BY);

    const generator = this.getTokenPaginationGenerator({ limit: 100, query, lastObjectId: undefined });

    const creatorIds = new Set<string>();
    for await (const batch of generator) {
      for (const parseCase of batch) {
        const id = parseCase.get(CaseTopology.CREATED_BY)?.id;
        if (id) creatorIds.add(id);
      }
    }

    return [...creatorIds];
  }

  public async getById(id: string): Promise<Case> {
    const parseCase = await this.subclasses.getSubclass(CaseTopology.TABLE).createWithoutData(id).fetch(this.options);

    return this.mapper.toDomain(parseCase);
  }

  public async save(caseObject: Case): Promise<Case> {
    const result = await this.mapper.fromDomain(caseObject).save(null, this.options);

    return this.mapper.toDomain(result);
  }

  public async countByOrganization(organizationId: string): Promise<number> {
    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = new this.parse.Query(WorkspaceTopology.TABLE);
      query.equalTo(
        WorkspaceTopology.ORGANIZATION,
        this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId)
      );
      query.ascending(ObjectTopology.ID);
      query.select(WorkspaceTopology.NUM_CASES); // Fetch objectIds and numCases
      query.limit(ParseCaseRepository.COUNT_CASES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);

      // Sum up the numCases from each workspace
      for (const workspace of results) {
        const numCases = workspace.get(WorkspaceTopology.NUM_CASES) || 0;
        total += numCases;
      }

      if (results.length < ParseCaseRepository.COUNT_CASES_PAGINATION_LIMIT) {
        continueQuerying = false;
      } else {
        lastId = results[results.length - 1].id;
      }
    }

    return total;
  }

  public async countByWorkspace(workspaceId: string, searchTerm?: string, searchContext?: CaseSearchContext): Promise<number> {
    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = this.buildCaseQuery(workspaceId, searchTerm, searchContext)
        .ascending(ObjectTopology.ID)
        .limit(ParseCaseRepository.COUNT_CASES_PAGINATION_LIMIT);

      query.select(); // Only fetch objectIds to minimize data transfer

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseCaseRepository.COUNT_CASES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  public async countByCaseType(caseTypeId: string): Promise<number> {
    const parseCaseType = this.subclasses.getSubclass(CaseTypeTopology.TABLE).createWithoutData(caseTypeId);

    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = new this.parse.Query(CaseTopology.TABLE)
        .equalTo(CaseTopology.CASE_TYPE, parseCaseType)
        .ascending(ObjectTopology.ID)
        .limit(ParseCaseRepository.COUNT_CASES_PAGINATION_LIMIT);

      query.select(); // Only fetch objectIds to minimize data transfer

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseCaseRepository.COUNT_CASES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  public async anyFromType(caseTypeId: string): Promise<boolean> {
    const parseCaseType = this.subclasses.getSubclass(CaseTypeTopology.TABLE).createWithoutData(caseTypeId);

    const result = await new this.parse.Query(CaseTopology.TABLE)
      .equalTo(CaseTopology.CASE_TYPE, parseCaseType)
      .select() // Only fetch objectIds to minimize data transfer
      .limit(1)
      .first(this.options);

    return result !== undefined;
  }

  public async list(
    limit: number,
    lastItem?: CaseListCursor,
    searchTerm?: string,
    workspaceId?: string,
    sorts?: CaseSortCriteria,
    filters?: CaseListFilter[],
    searchContext?: CaseSearchContext
  ): Promise<Case[]> {
    const query = this.buildCaseQuery(workspaceId, searchTerm, searchContext);

    // Determine the active sort. The first key drives the pagination cursor; default is createdAt desc.
    const sortEntries = sorts ? Object.entries(sorts) : [];
    const [activeField, activeDirection]: [keyof CaseListCursor, 'asc' | 'desc'] =
      sortEntries.length > 0
        ? [sortEntries[0][0] as keyof CaseListCursor, sortEntries[0][1]]
        : ['createdAt', 'desc'];

    // Apply sorts
    if (sortEntries.length > 0) {
      sortEntries.forEach(([sortKey, direction]) => {
        if (direction === 'asc') query.ascending(sortKey);
        else query.descending(sortKey);
      });
    } else {
      query.descending(ObjectTopology.CREATED_AT);
    }

    // Apply filters
    if (filters && filters.length > 0) {
      filters.forEach((filterItem) => {
        if (filterItem.before) {
          query.lessThan(filterItem.field, filterItem.before);
        }
        if (filterItem.after) {
          query.greaterThanOrEqualTo(filterItem.field, filterItem.after);
        }
        if (filterItem.equalTo) {
          query.equalTo(filterItem.field, filterItem.equalTo);
        }
        if (filterItem.contains) {
          query.matches(filterItem.field, new RegExp(this.escapeRegExp(filterItem.contains), 'i'));
        }
      });
    }

    if (lastItem) {
      const parseField = activeField === 'createdAt' ? ObjectTopology.CREATED_AT : activeField;
      const cursorValue = lastItem[activeField];
      if (activeDirection === 'asc') query.greaterThan(parseField, cursorValue);
      else query.lessThan(parseField, cursorValue);
    }
    query.limit(limit);

    const parseObjects = await query.find(this.options);

    return parseObjects.map((parseObject) => this.mapper.toDomain(parseObject));
  }

  public async delete(id: string): Promise<void> {
    const parseCase = this.subclasses.getSubclass(CaseTopology.TABLE).createWithoutData(id);
    await parseCase.save(
      {
        [SoftDeletableTopology.DELETED_AT]: new Date(),
      },
      this.options
    );
  }

  private buildCaseQuery(workspaceId?: string, searchTerm?: string, searchContext?: CaseSearchContext): Parse.Query {
    const query = new this.parse.Query(CaseTopology.TABLE);

    if (workspaceId) {
      const workspace = this.subclasses.getSubclass(WorkspaceTopology.TABLE).createWithoutData(workspaceId);
      query.equalTo(CaseTopology.WORKSPACE, workspace);
    }

    if (!!searchTerm && searchTerm !== '') {
      if (searchContext?.encrypted) {
        query.equalTo(CaseTopology.NAME, searchTerm);
        query.equalTo(
          CaseTopology.ORGANIZATION,
          this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(searchContext.organizationId)
        );
      } else {
        query.matches(CaseTopology.NAME, new RegExp(this.escapeRegExp(searchTerm), 'i'));
      }
    }

    return query;
  }
}
